import { Select } from '@ardoq/select';
import {
  DateRangeWithOptionalDates,
  GridPrecisionOptionsData,
  GridSelectOption,
  TimelineViewCommands,
  TimelineViewSettings,
} from '../../types';
import styled from 'styled-components';
import { DateTimePicker, DateTimePickerConfig } from '@ardoq/date-time-picker';
import {
  DropdownCustomContentOption,
  DropdownOptionType,
  getDropdownMenuPortalTarget,
} from '@ardoq/dropdown-menu';
import {
  GridPrecisionOptionLabels,
  TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE,
  getTimelinesGridPrecisionOptions,
  minimumDisplayedGridUnitsForGridPrecision,
} from '../../consts';
import { colors, s16, space, zIndex } from '@ardoq/design-tokens';
import { startOfDay } from '@ardoq/date-time';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import {
  TimelineGridPrecisionOptionValue,
  TimelinesGridPrecisionUnit,
  ViewIds,
} from '@ardoq/api-types';
import { WithPopover } from '@ardoq/popovers';
import { Icon, IconName } from '@ardoq/icons';
import {
  areTwoDateRangesEffectivelyEqual,
  readTimelineDomainRangeViewSetting,
  readTimelineGridPrecisionViewSetting,
} from '../../calculations';
import { getDateRangePickerConfigForTimeline } from './utils';
import { isManualGridOption } from '../../grid/calculations';
import { getCurrentLocale } from '@ardoq/locale';
import { noop } from 'lodash';

const DateRangePickerContainer = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${s16};
`;

const TimeFrameSelectionContainer = styled.div`
  padding: ${space.small};
`;

type AutoGridOptionRightContentArgs = {
  popoverText?: string;
};
const AutoGridOptionRightContent = ({
  popoverText = 'This automatically sets the optimal Timeline layout based on the available date range.',
}: AutoGridOptionRightContentArgs) => (
  <WithPopover content={popoverText}>
    <Icon iconName={IconName.INFO} style={{ color: colors.grey500 }} />
  </WithPopover>
);

/**
 * In case of Auto Layout, we should update the latest action stream state to the default value
 */
type ResetChartIfAutoLayoutArgs = {
  value: TimelineGridPrecisionOptionValue;
  instanceId?: string;
  commands: TimelineViewCommands;
};
const resetChartIfAutoLayout = ({
  value,
  instanceId,
  commands,
}: ResetChartIfAutoLayoutArgs) => {
  if (instanceId && value === TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE) {
    commands.resetChartWindow(instanceId);
  }
};

type GetDisabledOptionsPopoverContent = (
  value: TimelinesGridPrecisionUnit,
  label: string
) => string;

/**
 * For Discover caller it would be
 *  () => 'This Timeline layout is unavailable due to the date range set by the admin.';
 */
export const getDisabledOptionsPopoverContentForCoreApp: GetDisabledOptionsPopoverContent =
  (value: TimelinesGridPrecisionUnit, label: string) =>
    `Select a date range of at least ${
      minimumDisplayedGridUnitsForGridPrecision[value]
    } ${value} for the ${label.toLowerCase()} timeline layout`;

type GetGridSelectOptionsArgs = {
  availableGridPrecisions: TimelineGridPrecisionOptionValue[];
  activeGridOption: TimelineGridPrecisionOptionValue;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  getDisabledOptionsPopoverContent: GetDisabledOptionsPopoverContent;
  viewInstanceId?: string;
  commands: TimelineViewCommands;
  gridPrecisionOptionLabels?: GridPrecisionOptionLabels;
  autoLayoutInfoPopover?: string;
};
const getGridSelectOptions = ({
  availableGridPrecisions,
  activeGridOption,
  onViewSettingsUpdate,
  getDisabledOptionsPopoverContent,
  viewInstanceId,
  commands,
  gridPrecisionOptionLabels,
  autoLayoutInfoPopover,
}: GetGridSelectOptionsArgs): GridSelectOption[] =>
  getTimelinesGridPrecisionOptions(gridPrecisionOptionLabels).map(
    ([value, label]) => {
      const isUserDefinedGrid = isManualGridOption(value);
      const isDisabled =
        isUserDefinedGrid && !availableGridPrecisions.includes(value);

      const content =
        isUserDefinedGrid && isDisabled ? (
          <WithPopover content={getDisabledOptionsPopoverContent(value, label)}>
            <option disabled={isDisabled}>{label}</option>
          </WithPopover>
        ) : (
          label
        );

      const handleGridOptionClick = () => {
        if (value !== activeGridOption) {
          // TODO: @Goetz - the view settings update disregards the view instance id, so why do we namespace the command?
          resetChartIfAutoLayout({
            value,
            instanceId: viewInstanceId,
            commands,
          });

          onViewSettingsUpdate(
            ViewIds.TIMELINE,
            {
              gridPrecision: value,
            },
            true
          );
        }
      };

      return {
        value,
        label: content as string, // https://ardoqcom.atlassian.net/browse/ARD-18787 will remove this cast
        isDisabledWithPopover: isDisabled,
        isActive: value === activeGridOption,
        onClick: isDisabled ? noop : handleGridOptionClick,
        rightContent: isManualGridOption(value) ? undefined : (
          <AutoGridOptionRightContent popoverText={autoLayoutInfoPopover} />
        ),
        type: DropdownOptionType.OPTION,
      };
    }
  );

const getActiveGridOptionValue = (
  viewSettings: TimelineViewSettings,
  adjustedGridPrecision: TimelinesGridPrecisionUnit
) => {
  const gridPrecisionViewSettingsValue = readTimelineGridPrecisionViewSetting(
    viewSettings.gridPrecision
  );

  return isManualGridOption(gridPrecisionViewSettingsValue)
    ? adjustedGridPrecision
    : gridPrecisionViewSettingsValue;
};

type GetGridPrecisionOptionsDataArgs = {
  viewInstanceId: string;
  commands: TimelineViewCommands;
  availableGridPrecisions: TimelineGridPrecisionOptionValue[];
  viewSettings: TimelineViewSettings;
  adjustedGridPrecision: TimelinesGridPrecisionUnit;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  getDisabledOptionsPopoverContent: GetDisabledOptionsPopoverContent;
  gridPrecisionOptionLabels?: GridPrecisionOptionLabels;
  autoLayoutInfoPopover?: string;
};

export const getGridPrecisionOptionsData = ({
  viewInstanceId,
  commands,
  availableGridPrecisions,
  viewSettings,
  adjustedGridPrecision,
  onViewSettingsUpdate,
  getDisabledOptionsPopoverContent,
  gridPrecisionOptionLabels,
  autoLayoutInfoPopover,
}: GetGridPrecisionOptionsDataArgs) => {
  const activeGridOption = getActiveGridOptionValue(
    viewSettings,
    adjustedGridPrecision
  );

  return {
    value: activeGridOption,
    options: getGridSelectOptions({
      activeGridOption,
      availableGridPrecisions,
      onViewSettingsUpdate,
      viewInstanceId,
      commands,
      getDisabledOptionsPopoverContent,
      gridPrecisionOptionLabels,
      autoLayoutInfoPopover,
    }),
  };
};

type GridPrecisionInputArgs = {
  gridPrecisionOptionsData: GridPrecisionOptionsData;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  timelineLayoutSelectLabel?: string;
};
const GridPrecisionInput = ({
  gridPrecisionOptionsData,
  onViewSettingsUpdate,
  timelineLayoutSelectLabel = 'Timeline layout',
}: GridPrecisionInputArgs) => (
  <div style={{ marginTop: s16 }}>
    <Select
      styles={{
        menuPortal: (base: object) => ({
          ...base,
          zIndex: zIndex.MODAL_BACKDROP + 1,
        }),
      }}
      label={timelineLayoutSelectLabel}
      menuPlacement={'bottom'}
      menuPortalTarget={getDropdownMenuPortalTarget()}
      onValueChange={value => {
        onViewSettingsUpdate(
          ViewIds.TIMELINE,
          {
            gridPrecision: value,
          },
          true
        );
      }}
      {...gridPrecisionOptionsData}
    />
  </div>
);

type TimelineDateRangePickerArgs = {
  viewSettings: TimelineViewSettings;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  labelText?: string;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
};

const TimelineDateRangePicker = ({
  viewSettings,
  onViewSettingsUpdate,
  labelText,
  startDatePlaceholder,
  endDatePlaceholder,
}: TimelineDateRangePickerArgs) => {
  const domainRangeViewSetting = readTimelineDomainRangeViewSetting(
    viewSettings.domainRange
  );

  return (
    <DateRangePicker
      domain={domainRangeViewSetting}
      onViewSettingsUpdate={onViewSettingsUpdate}
      labelText={labelText}
      startDatePlaceholder={startDatePlaceholder}
      endDatePlaceholder={endDatePlaceholder}
      dateRangePickerConfig={getDateRangePickerConfigForTimeline(
        domainRangeViewSetting
      )}
    />
  );
};

type DateRangePickerConfig = {
  startDateConfig?: DateTimePickerConfig;
  endDateConfig?: DateTimePickerConfig;
};
type DateRangePickerArgs = {
  domain: DateRangeWithOptionalDates;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  labelText?: string;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  dateRangePickerConfig?: DateRangePickerConfig;
};

const DateRangePicker = ({
  domain: [domainStart, domainEnd],
  onViewSettingsUpdate,
  labelText,
  startDatePlaceholder = 'Start date...',
  endDatePlaceholder = 'End date...',
  dateRangePickerConfig,
}: DateRangePickerArgs) => {
  const updateDateRange = (newDomainRange: DateRangeWithOptionalDates) => {
    // remove time from input
    const roundedDomainRange = newDomainRange.map(
      value => value && startOfDay(value)
    ) as DateRangeWithOptionalDates;

    if (
      !areTwoDateRangesEffectivelyEqual(
        [domainStart, domainEnd],
        roundedDomainRange
      )
    ) {
      onViewSettingsUpdate(
        ViewIds.TIMELINE,
        {
          domainRange: roundedDomainRange,
        },
        true
      );
    }
  };

  // We need to align to flex start, for error message not to break the layout => empty label
  // This date time picker is temporary, it will be replaced soon, and I will not change all
  // the types for just this one case, so with this cast I sin.
  const placeholder = labelText
    ? ((<span style={{ opacity: 0 }}>Placeholder</span>) as unknown as string)
    : undefined;

  const baseConfig = { locale: getCurrentLocale() };
  return (
    <DateRangePickerContainer>
      <DateTimePicker
        label={labelText}
        placeholder={startDatePlaceholder}
        date={domainStart}
        config={{ ...baseConfig, ...dateRangePickerConfig?.startDateConfig }}
        onChange={value => updateDateRange([value, domainEnd])}
      />
      <DateTimePicker
        label={placeholder}
        placeholder={endDatePlaceholder}
        date={domainEnd}
        config={{ ...baseConfig, ...dateRangePickerConfig?.endDateConfig }}
        onChange={value => updateDateRange([domainStart, value])}
      />
    </DateRangePickerContainer>
  );
};

type TimeFrameSelectionArgs = {
  gridPrecisionOptionsData: GridPrecisionOptionsData;
  viewSettings: TimelineViewSettings;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  dateRangeLabel?: string;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  timelineLayoutSelectLabel?: string;
};
const TimeFrameSelection = ({
  viewSettings,
  onViewSettingsUpdate,
  gridPrecisionOptionsData,
  dateRangeLabel = 'Date range',
  startDatePlaceholder,
  endDatePlaceholder,
  timelineLayoutSelectLabel,
}: TimeFrameSelectionArgs) => (
  <TimeFrameSelectionContainer>
    <TimelineDateRangePicker
      labelText={dateRangeLabel}
      viewSettings={viewSettings}
      onViewSettingsUpdate={onViewSettingsUpdate}
      startDatePlaceholder={startDatePlaceholder}
      endDatePlaceholder={endDatePlaceholder}
    />
    <GridPrecisionInput
      gridPrecisionOptionsData={gridPrecisionOptionsData}
      onViewSettingsUpdate={onViewSettingsUpdate}
      timelineLayoutSelectLabel={timelineLayoutSelectLabel}
    />
  </TimeFrameSelectionContainer>
);

export const getTimeFrameSelectionDropdownOptions = (
  args: TimeFrameSelectionArgs
): DropdownCustomContentOption[] => [
  {
    content: <TimeFrameSelection key={'time-frame-selection'} {...args} />,
    type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
  },
];
